import React, { useState } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PlanCard from './components/PlanCard';
import PlanFormModal from './components/PlanFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const SubscriptionPlansManagement = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(null);
  const [plans, setPlans] = useState([
    {
      id: 1,
      name: 'Free',
      description: 'Perfect for getting started',
      monthlyPrice: 0,
      yearlyPrice: 0,
      status: 'active',
      features: {
        bmc: true,
        predictiveAnalysis: false,
        goalIntelligence: false,
        decisionIntelligence: false,
        reports: false
      },
      limits: {
        reportDownloads: 0,
        aiUsagePerMonth: 10
      },
      subscribers: 245
    },
    {
      id: 2,
      name: 'Growth',
      description: 'For growing businesses',
      monthlyPrice: 2499,
      yearlyPrice: 24999,
      status: 'active',
      features: {
        bmc: true,
        predictiveAnalysis: true,
        goalIntelligence: false,
        decisionIntelligence: false,
        reports: true
      },
      limits: {
        reportDownloads: 10,
        aiUsagePerMonth: 100
      },
      subscribers: 89
    },
    {
      id: 3,
      name: 'Scale',
      description: 'For scaling enterprises',
      monthlyPrice: 6799,
      yearlyPrice: 67999,
      status: 'active',
      features: {
        bmc: true,
        predictiveAnalysis: true,
        goalIntelligence: true,
        decisionIntelligence: true,
        reports: true
      },
      limits: {
        reportDownloads: 50,
        aiUsagePerMonth: 500
      },
      subscribers: 34
    },
    {
      id: 4,
      name: 'Enterprise',
      description: 'Custom solutions for large organizations',
      monthlyPrice: 15999,
      yearlyPrice: 159999,
      status: 'active',
      features: {
        bmc: true,
        predictiveAnalysis: true,
        goalIntelligence: true,
        decisionIntelligence: true,
        reports: true
      },
      limits: {
        reportDownloads: -1,
        aiUsagePerMonth: -1
      },
      subscribers: 12
    }
  ]);

  const handleCreatePlan = () => {
    setEditingPlan(null);
    setIsFormModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setIsFormModalOpen(true);
  };

  const handleDeletePlan = (plan) => {
    setDeletingPlan(plan);
    setIsDeleteModalOpen(true);
  };

  const handleSavePlan = (planData) => {
    if (editingPlan) {
      setPlans(plans?.map(p => p?.id === editingPlan?.id ? { ...planData, id: editingPlan?.id, subscribers: editingPlan?.subscribers } : p));
    } else {
      setPlans([...plans, { ...planData, id: Date.now(), subscribers: 0 }]);
    }
    setIsFormModalOpen(false);
    setEditingPlan(null);
  };

  const handleConfirmDelete = () => {
    setPlans(plans?.filter(p => p?.id !== deletingPlan?.id));
    setIsDeleteModalOpen(false);
    setDeletingPlan(null);
  };

  const totalSubscribers = plans?.reduce((sum, plan) => sum + (plan?.subscribers || 0), 0);
  const activePlans = plans?.filter(p => p?.status === 'active')?.length;
  const totalMRR = plans?.reduce((sum, plan) => sum + (plan?.monthlyPrice * (plan?.subscribers || 0)), 0);

  return (
    <div className="flex h-screen bg-background">
      <AdminSidebar isCollapsed={isSidebarCollapsed} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <AuthHeader
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  Subscription Plans
                </h1>
                <p className="text-muted-foreground font-body mt-1">
                  Manage pricing, features, and limits for all subscription tiers
                </p>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Plus"
                iconPosition="left"
                onClick={handleCreatePlan}
              >
                Create Plan
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-lg p-6 elevation-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground font-caption">
                    Total Subscribers
                  </span>
                  <Icon name="Users" size={20} className="text-primary" />
                </div>
                <p className="text-3xl font-heading font-bold text-foreground">
                  {totalSubscribers}
                </p>
                <p className="text-xs text-success font-caption mt-1">
                  Across all plans
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6 elevation-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground font-caption">
                    Active Plans
                  </span>
                  <Icon name="Package" size={20} className="text-success" />
                </div>
                <p className="text-3xl font-heading font-bold text-foreground">
                  {activePlans}
                </p>
                <p className="text-xs text-muted-foreground font-caption mt-1">
                  Out of {plans?.length} total
                </p>
              </div>

              <div className="bg-card border border-border rounded-lg p-6 elevation-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground font-caption">
                    Monthly Revenue
                  </span>
                  <Icon name="DollarSign" size={20} className="text-accent" />
                </div>
                <p className="text-3xl font-heading font-bold text-foreground">
                  ₹{(totalMRR / 1000)?.toFixed(1)}K
                </p>
                <p className="text-xs text-success font-caption mt-1">
                  From subscriptions
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans?.map((plan) => (
                <PlanCard
                  key={plan?.id}
                  plan={plan}
                  onEdit={() => handleEditPlan(plan)}
                  onDelete={() => handleDeletePlan(plan)}
                />
              ))}
            </div>
          </div>
        </main>
      </div>
      <PlanFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingPlan(null);
        }}
        plan={editingPlan}
        onSave={handleSavePlan}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingPlan(null);
        }}
        plan={deletingPlan}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default SubscriptionPlansManagement;